import type { MessageDescriptor } from 'react-intl';
import { useOptionalHelpCenterType } from '../contexts/help-center-type-context';
import breadcrumbMessages from '../ui/settings-breadcrumbs/messages';
import sidebarMessages from '../ui/sidebar/messages';
import sidebarHeaderMessages from '../ui/sidebar/sidebar-help-center-header/messages';

/**
 * Centralized message dictionary for Help Center vs Help Hub copy.
 * Returns pre-selected MessageDescriptors based on help center type.
 *
 * This follows the pattern from hub-theming-config-panel where all conditional
 * message selection is centralized in one place instead of scattered ternaries.
 *
 * Uses useOptionalHelpCenterType internally so it works both inside and outside
 * HelpCenterTypeProvider context. When outside the provider, defaults to Help Center messages.
 *
 * @example
 * ```tsx
 * const messages = useHelpCenterMessageDictionary();
 * return <div>{formatMessage(messages.backButton)}</div>
 * ```
 */
export const useHelpCenterMessageDictionary = () => {
	const { isHelpHub } = useOptionalHelpCenterType();

	return {
		// Sidebar messages
		backButton: isHelpHub ? sidebarMessages.backButtonHelpHub : sidebarMessages.backButton,
		backButtonHref: isHelpHub ? '/hubs' : '/',
		settingsLabel: isHelpHub ? sidebarMessages.settingsLabelHelpHub : sidebarMessages.settingsLabel,

		// Breadcrumb messages
		breadcrumbRoot: isHelpHub
			? breadcrumbMessages.settingsPageBreadcrumbItemHelpHub
			: breadcrumbMessages.settingsPageBreadcrumbItem,

		// Sidebar header
		headerLabel: isHelpHub
			? sidebarHeaderMessages.helpHubLabel
			: sidebarHeaderMessages.helpCenterLabel,
	};
};

/**
 * Helper to select the appropriate message based on help center type.
 * Use this in components that have their own message files.
 *
 * Works both inside and outside HelpCenterTypeProvider context.
 * When outside the provider (e.g., creating new help centers), defaults to Help Center messages.
 *
 * @example
 * ```tsx
 * const nameLabel = useConditionalMessage(messages.helpHubNameTextLabel, messages.helpCenterNameTextLabel);
 * return <Field label={formatMessage(nameLabel)} />;
 * ```
 */
export const useConditionalMessage = (
	helpHubMessage: MessageDescriptor,
	helpCenterMessage: MessageDescriptor,
): MessageDescriptor => {
	const { isHelpHub } = useOptionalHelpCenterType();
	return isHelpHub ? helpHubMessage : helpCenterMessage;
};
